import { Button } from '@shadcn/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { cn } from '@shadcn/ui/lib/utils';
import {
    CREATE_CONTACTS_INTERACTIONS,
    GET_CONTACTS_INTERACTIONS,
    GET_CONTACTS_WEIGHTED_SCORES,
    MARK_ACTION_AS_NOT_INTERESTED,
    UNDO_MARK_ACTION_AS_NOT_INTERESTED,
    GetContactsWeightedScoresQuery,
} from '@zaplify/graphql';
import { Check, X } from 'lucide-react';
import { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { getUserInteractionWeights, ContactClassificationMetadata } from '@andsend/intelligence-shared';

interface QuickNotInterestedButtonProps {
    contact: GetContactsWeightedScoresQuery['contacts_weighted_scores'][number];
    onNotInterested?: (e: React.MouseEvent) => void;
    variant?: 'default' | 'ghost' | 'outline';
    size?: 'sm' | 'icon-sm' | 'icon';
    className?: string;
    showLabel?: boolean;
}

export const QuickNotInterestedButton: FC<QuickNotInterestedButtonProps> = ({
    contact,
    onNotInterested,
    variant = 'ghost',
    size = 'icon-sm',
    className,
    showLabel = false,
}) => {
    const [markActionAsNotInterested] = useMutation(MARK_ACTION_AS_NOT_INTERESTED);
    const [undoMarkActionAsNotInterested] = useMutation(UNDO_MARK_ACTION_AS_NOT_INTERESTED);
    const [createContactsScoringWeights] = useMutation(CREATE_CONTACTS_INTERACTIONS);
    const { toast } = useToast();

    const handleNotInterested = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onNotInterested?.(e);

        if (contact.userSetAsNotInterestedAt) {
            undoMarkActionAsNotInterested({
                variables: { contactId: contact.id },
                refetchQueries: [GET_CONTACTS_WEIGHTED_SCORES],
            }).then(() => {
                toast({
                    title: 'Undone not interested',
                });
            });
        } else {
            markActionAsNotInterested({
                variables: { contactId: contact.id },
                refetchQueries: [GET_CONTACTS_WEIGHTED_SCORES],
            }).then(() => {
                toast({
                    title: 'Marked as not interested',
                    description:
                        "You've taught the agent that this was a bad action. There will be no more actions for this contact until a signal is detected.",
                    action: (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-fit ml-auto"
                            onClick={() => {
                                undoMarkActionAsNotInterested({
                                    variables: { contactId: contact.id },
                                }).then(() => {
                                    toast({
                                        title: 'Undone not interested',
                                    });
                                });
                            }}
                        >
                            Undo
                        </Button>
                    ),
                });
            });
        }

        const weights = getUserInteractionWeights(contact.metadata as ContactClassificationMetadata, -0.1);

        createContactsScoringWeights({
            variables: {
                userId: contact.userId || '',
                organizationId: contact.organizationId || '',
                contactId: contact.id,
                contactMetadata: contact.metadata || {},
                weights: weights,
                interactionType: 'NOT_INTERESTED',
            },
            refetchQueries: [GET_CONTACTS_INTERACTIONS],
        });
    };

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant={variant}
                    size={size}
                    onClick={handleNotInterested}
                    className={cn(className, contact.userSetAsNotInterestedAt && 'text-green-600')}
                >
                    {contact.userSetAsNotInterestedAt ? (
                        <Check
                            className={cn(
                                'h-4 w-4 flex-shrink-0',
                                variant === 'outline' ? 'text-current' : 'text-gray-500'
                            )}
                        />
                    ) : (
                        <X
                            className={cn(
                                'h-4 w-4 flex-shrink-0',
                                variant === 'outline' ? 'text-current' : 'text-gray-500'
                            )}
                        />
                    )}
                    {showLabel && (
                        <span className="hidden lg:inline">
                            {contact.userSetAsNotInterestedAt ? 'Undo not interested' : 'Not interested'}
                        </span>
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                <p>{contact.userSetAsNotInterestedAt ? 'Undo not interested' : 'Not interested'}</p>
            </TooltipContent>
        </Tooltip>
    );
};
